import { createEntityAdapter, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@shared/hooks';
import { Tag } from '@src/api/common.ts';

const tagAdapter = createEntityAdapter({
  selectId: (tag: Tag) => tag.id,
});

const tagSlice = createSlice({
  name: 'common/tags',
  initialState: tagAdapter.getInitialState({
    selectedTags: [] as Tag[],
  }),
  reducers: {
    tagsSet: tagAdapter.setAll,
    selectedTagsSet: (state, action: PayloadAction<Tag[]>) => {
      state.selectedTags = [...action.payload];
    },
    resetSelectedTags: (state) => {
      state.selectedTags = [];
    },
  },
});

export const tagReducer = tagSlice.reducer;
export const tagActions = tagSlice.actions;

const tagSelectors = tagAdapter.getSelectors();

export const useTags = () => {
  const dispatch = useAppDispatch();

  const tags = useAppSelector((state) => tagSelectors.selectAll(state.common.tags));
  const selectedTags = useAppSelector((state) => state.common.tags.selectedTags);

  const setTags = useCallback((tags: Tag[]) => dispatch(tagActions.tagsSet(tags)), [dispatch]);
  const setSelectedTags = useCallback(
    (tags: Tag[]) => dispatch(tagActions.selectedTagsSet(tags)),
    [dispatch]
  );
  const resetSelectedTags = useCallback(() => dispatch(tagActions.resetSelectedTags()), [dispatch]);

  const onSelect = useCallback(
    (tag: Tag) => {
      let tags = [...selectedTags];
      const idx = selectedTags.findIndex((storedTag) => storedTag.id === tag.id);
      if (idx === -1) {
        tags = [...selectedTags, tag];
      } else {
        tags.splice(idx, 1);
      }
      setSelectedTags(tags);
    },
    [selectedTags, setSelectedTags]
  );

  const isSelected = useCallback(
    (tag: Tag) => {
      const tags = [...selectedTags];
      const idx = tags.findIndex((storedTag) => storedTag.id === tag.id);
      return idx !== -1;
    },
    [selectedTags]
  );

  return { tags, setTags, setSelectedTags, onSelect, isSelected, selectedTags, resetSelectedTags };
};


